
import React, { useState } from 'react';
import { Page } from '../types';
import { useAuth } from '../context/AuthContext';
import { ShieldCheckIcon } from '../assets/icons';

interface LoginPageProps {
    onNavigate: (page: Page) => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onNavigate }) => {
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const success = await login(email, password);
            if (success) {
                onNavigate('admin'); // Redirect to Admin/Command Center on success
            } else {
                setError('Credenciais inválidas. Tente "admin@arsenal.com" / "arsenal"');
            }
        } catch (err) {
            setError('Erro ao autenticar. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-arsenal-red/20 rounded-full blur-3xl filter opacity-20 animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl filter opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            <div className="max-w-md w-full space-y-8 relative z-10 p-8 glass-dark rounded-2xl border border-gray-700/50 shadow-2xl backdrop-blur-xl">
                <div className="text-center">
                    <div className="mx-auto h-16 w-16 bg-gradient-to-br from-arsenal-red to-red-900 rounded-xl flex items-center justify-center shadow-lg shadow-red-900/30 transform rotate-45 mb-6">
                        <ShieldCheckIcon className="h-8 w-8 text-white transform -rotate-45" />
                    </div>
                    <h2 className="mt-2 text-3xl font-black text-white tracking-tight uppercase">
                        Acesso Restrito
                    </h2>
                    <p className="mt-2 text-sm text-gray-400">
                        Identifique-se para acessar o Command Center.
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <input type="hidden" name="remember" value="true" />
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div className="mb-4">
                            <label htmlFor="email-address" className="sr-only">Email</label>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="appearance-none rounded-xl relative block w-full px-4 py-3 border border-gray-700 placeholder-gray-500 text-white bg-gray-800/50 focus:outline-none focus:ring-arsenal-red focus:border-arsenal-red focus:z-10 sm:text-sm transition-all"
                                placeholder="Email Corporativo"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Senha</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="appearance-none rounded-xl relative block w-full px-4 py-3 border border-gray-700 placeholder-gray-500 text-white bg-gray-800/50 focus:outline-none focus:ring-arsenal-red focus:border-arsenal-red focus:z-10 sm:text-sm transition-all"
                                placeholder="Senha de Acesso"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="rounded-lg bg-red-900/30 border border-red-500/50 p-3">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-red-400">{error}</h3>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white uppercase tracking-wider ${isLoading ? 'bg-gray-600 cursor-not-allowed' : 'bg-arsenal-red hover:bg-red-700 shadow-lg shadow-red-900/50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arsenal-red focus:ring-offset-gray-900 transition-all duration-300`}
                        >
                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                <ShieldCheckIcon className="h-5 w-5 text-red-300 group-hover:text-red-100 transition-colors" />
                            </span>
                            {isLoading ? 'Autenticando...' : 'Acessar Sistema'}
                        </button>
                    </div>
                </form>

                <div className="text-center mt-4">
                    <button onClick={() => { }} className="text-xs text-gray-500 hover:text-white transition-colors">
                        Esqueceu sua credencial? Contate o TI Tático.
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
